import produce from 'immer';
import { MWC_FILTER_TYPES } from '@common/Filters/constants/filterTypes';
import latinize from '@helpers/latinize';
import sortByPropertyValue from '@helpers/sortByPropertyValue';

const getFilterItemCount = (item, items) => {
    const availableFilter = items?.find(currentItem => currentItem.value === item.value);
    return availableFilter?.count || 0;
};

const getFilterItemParentValue = (item, brandId) => {
    const values = item.value.split('-');

    if (brandId && values.includes(brandId)) {
        return brandId;
    }

    return values[0];
};

/**
 * Get filter count from availableFilters
 *
 * @param {array} filterArray
 * @param {array} availableFilterItems
 * @param {String} activeBrandId
 * @returns {array}
 */
export const getCount = (filterArray, availableFilterItems, activeBrandId = '0') => (
    filterArray.map(filterItem => ({
        ...filterItem,
        count: getFilterItemCount(filterItem, availableFilterItems),
        parentValue: getFilterItemParentValue(filterItem, activeBrandId)
    }))
);

export default getCount;

const isSelectedItem = (filter, activeFilters) => activeFilters.some(active => (
    active.value === filter.value && active.filterType === filter.filterType
));

/**
 * Get selected filters from activeFilters
 *
 * @param {array} filterArray
 * @param {array} activeFilters
 * @returns {array}
 */
export const getSelected = (filterArray, activeFilters) => (
    filterArray.map(filterItem => ({ ...filterItem, isSelected: isSelectedItem(filterItem, activeFilters) }))
);

export const injectMadeWithCareDescription = (filterArray, description) => (
    filterArray.map(filterItem => (
        { ...filterItem, description: MWC_FILTER_TYPES.includes(filterItem.filterType) ? description : '' }))
);

/**
 * Prepare brand filters for display
 *
 * @param {array} brandFilters
 * @param {array} availableBrandFilterItems
 * @param {string} searchQuery
 * @returns {array}
 */
export const prepareBrandsFilters = (brandFilters, availableBrandFilterItems, searchQuery) => {
    const latinizedSearchQuery = latinize(searchQuery).toLowerCase();
    let brands = brandFilters.map(item => produce(item, (draftItem) => {
        draftItem.nameLatinized = latinize(item.name).toLowerCase();
    }));

    if (latinizedSearchQuery !== '') {
        brands = brands.filter(item => item.nameLatinized.indexOf(latinizedSearchQuery) !== -1);
    }

    return getCount(brands, availableBrandFilterItems);
};

/**
 * Prepare the filter groups for display
 *
 * @param rawItems
 * @param availableItems
 * @returns {object}
 */
export const getGroups = (rawItems, availableItems) => {
    const { groups } = rawItems;

    return groups.reduce((acc, group) => {
        const groupRanges = group.ranges;

        acc.list.push(group.label);

        // get count from availableFilters
        const newGroupRanges = getCount(groupRanges, availableItems);

        acc.items[group.label] = newGroupRanges.sort(sortByPropertyValue('sortOrder'));

        return acc;
    }, { list: [], items: {} });
};

/**
 * Prepare filter items for display
 *
 * @param rawItems
 * @param availableItems
 * @param isSortable
 * @returns {Array}
 */
export const getItems = ({ rawItems, availableItems, isSortable }) => {
    let newFilterArray = rawItems.options ? [...rawItems.options] : [...rawItems];

    newFilterArray = getCount(newFilterArray, availableItems);

    if (isSortable) {
        newFilterArray.sort(sortByPropertyValue('sortOrder'));
    }

    return newFilterArray;
};

export const getDropdownIcon = (type, theme, isExpanded) => {
    if (type === 'order' && theme === 'mod1') {
        return 'palette_sort';
    }

    if (isExpanded) {
        return 'palette_chevron_up';
    }

    return 'palette_chevron_down';
};

/**
 * Prepare data for palette filters
 *
 * @param {array} rawFilters
 * @param {array} availableFilters
 * @param {array} activeFilters
 * @param {String} activeBrandId
 * @returns {array}
 */
export const formatFilterItems = ({
    rawFilters,
    availableFilters,
    activeFilters,
    activeBrandId = 0
}) => rawFilters
    .map(filter => ({
        ...filter,
        count: getFilterItemCount(filter, availableFilters),
        parentValue: getFilterItemParentValue(filter, activeBrandId),
        isSelected: isSelectedItem(filter, activeFilters)
    }))
    .filter(item => item?.count > 0);
